import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  Select,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import SaveIcon from '@material-ui/icons/Save';
import ListAltOutlinedIcon from '@material-ui/icons/ListAltOutlined';
import SettingsCard from 'components/kea/SettingsCard/SettingsCard';
import { serviceRegistry } from '../../store';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import CategoryExclusionPicker from './CategoryExclusionPicker';

const MODE_ALL = 'all';
const MODE_ACTIVE = 'active';
const MODE_AVAILABLE = 'available';
const MODE_EXCLUDED = 'excluded';

const MODE_MESSAGE_KEYS = {
  [MODE_ALL]: {
    title: 'search.page.dataSettings.mode.all.title',
    description: 'search.page.dataSettings.mode.all.description',
  },
  [MODE_ACTIVE]: {
    title: 'search.page.dataSettings.mode.active.title',
    description: 'search.page.dataSettings.mode.active.description',
  },
  [MODE_AVAILABLE]: {
    title: 'search.page.dataSettings.mode.available.title',
    description: 'search.page.dataSettings.mode.available.description',
  },
  [MODE_EXCLUDED]: {
    title: 'search.page.dataSettings.mode.excluded.title',
    description: 'search.page.dataSettings.mode.excluded.description',
  },
};

const RULE_MESSAGE_KEYS = {
  ps_quantity_gt_zero: {
    title: 'search.page.dataSettings.rule.quantity.title',
    description: 'search.page.dataSettings.rule.quantity.description',
  },
  quantity_gt_zero: {
    title: 'search.page.dataSettings.rule.quantity.title',
    description: 'search.page.dataSettings.rule.quantity.description',
  },
  ps_stock_flag: {
    title: 'search.page.dataSettings.rule.stockFlag.title',
    description: 'search.page.dataSettings.rule.stockFlag.description',
  },
  stock_flag: {
    title: 'search.page.dataSettings.rule.stockFlag.title',
    description: 'search.page.dataSettings.rule.stockFlag.description',
  },
  ps_available_for_order: {
    title: 'search.page.dataSettings.rule.availableForOrder.title',
    description: 'search.page.dataSettings.rule.availableForOrder.description',
  },
  available_for_order: {
    title: 'search.page.dataSettings.rule.availableForOrder.title',
    description: 'search.page.dataSettings.rule.availableForOrder.description',
  },
  ps_combination_in_stock: {
    title: 'search.page.dataSettings.rule.combination.title',
    description: 'search.page.dataSettings.rule.combination.description',
  },
  combination_in_stock: {
    title: 'search.page.dataSettings.rule.combination.title',
    description: 'search.page.dataSettings.rule.combination.description',
  },
};

const PROVIDER_MESSAGE_KEYS = {
  attribute: {
    title: 'search.page.dataSettings.provider.attributes.title',
    description: 'search.page.dataSettings.provider.attributes.description',
  },
  attributes: {
    title: 'search.page.dataSettings.provider.attributes.title',
    description: 'search.page.dataSettings.provider.attributes.description',
  },
  feature: {
    title: 'search.page.dataSettings.provider.features.title',
    description: 'search.page.dataSettings.provider.features.description',
  },
  features: {
    title: 'search.page.dataSettings.provider.features.title',
    description: 'search.page.dataSettings.provider.features.description',
  },
};

// The plugin now materializes provider data and the public result projector
// exposes it as product.attributes for every indexed hydration path.
const PAYLOAD_PROVIDER_UI_ENABLED = true;

const useStyles = makeStyles((theme) => ({
  root: {
    color: '#1e2248',
    fontFamily: "'Roboto', sans-serif",
    '& .MuiTypography-h6': {
      color: '#1e2248',
      fontSize: 18,
      fontWeight: 600,
      letterSpacing: '-0.015em',
      lineHeight: 1.35,
    },
    '& .MuiTypography-subtitle1, & .MuiTypography-subtitle2': {
      color: '#1e2248',
      fontWeight: 600,
    },
    '& .MuiTypography-body2': {
      color: '#66708a',
      fontSize: 13,
      lineHeight: 1.55,
    },
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  intro: {
    maxWidth: 680,
    marginBottom: theme.spacing(2.5),
  },
  options: {
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  optionGroup: {
    display: 'grid',
    gap: theme.spacing(1),
  },
  option: {
    display: 'flex',
    alignItems: 'flex-start',
    border: '1px solid #ddeae5',
    borderRadius: 8,
    padding: theme.spacing(1.75, 2),
    cursor: 'pointer',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
      borderColor: '#b5d8cb',
      backgroundColor: '#fbfefd',
    },
    '&:focus-within': {
      boxShadow: '0 0 0 3px rgba(var(--kea-color-primary-rgb), 0.12)',
    },
  },
  selectedOption: {
    borderColor: '#8fcbb4',
    backgroundColor: '#f3fbf8',
  },
  optionRadio: {
    padding: theme.spacing(0.25, 1.5, 0.25, 0),
  },
  optionBody: {
    flex: 1,
    minWidth: 0,
  },
  optionTitle: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  recommended: {
    height: 22,
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--kea-color-primary)',
    backgroundColor: 'var(--kea-color-primary-soft)',
  },
  nested: {
    margin: theme.spacing(0, 0, 0, 4.5),
    padding: theme.spacing(2),
    borderLeft: '3px solid #8fcbb4',
    backgroundColor: '#f9fffd',
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
    },
  },
  ruleSelect: {
    minWidth: 320,
    maxWidth: '100%',
  },
  ruleDescription: {
    maxWidth: 620,
    marginTop: theme.spacing(1),
  },
  variantHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  automatic: {
    flexShrink: 0,
    color: theme.palette.success?.dark || '#1b5e20',
    backgroundColor: '#e8f5e9',
    fontWeight: 600,
  },
  providerIdentity: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  providerIcon: {
    display: 'flex',
    width: 40,
    height: 40,
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    background: 'var(--kea-color-primary-soft)',
    color: 'var(--kea-color-primary)',
  },
  providerCopy: {
    maxWidth: 650,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  providerDescription: {
    marginTop: theme.spacing(0.5),
  },
  providerOptions: {
    display: 'grid',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  providerRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    margin: 0,
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    '&:hover': {
      background: theme.palette.action.hover,
    },
    '&:focus-within': {
      outline: '2px solid var(--kea-color-primary)',
      outlineOffset: 2,
    },
  },
  providerLabel: {
    flex: 1,
    minWidth: 0,
    marginLeft: theme.spacing(1),
    overflowWrap: 'anywhere',
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  headerActionRow: {
    marginTop: 0,
  },
}));

function descriptorKey(item, type) {
  if (type === 'selector') return item?.selector_key || item?.key || '';
  if (type === 'rule') return item?.available_rule_key || item?.rule_key || item?.key || '';
  return item?.provider_key || item?.key || '';
}

function selectorMode(key) {
  if (key === 'products_all') return MODE_ALL;
  if (key === 'products_active_only') return MODE_ACTIVE;
  if (key === 'products_available_only' || key === 'available_only') return MODE_AVAILABLE;
  if (key === 'categories_excluded') return MODE_EXCLUDED;
  return null;
}

function categoryId(category) {
  const raw = category?.id ?? category?.category_id;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : raw;
}

function indexingSnapshotOf({
  mode, categories, ruleKey, ruleParams, fallbackWhenAbsent, providerKeys, providerConfigs,
}) {
  return JSON.stringify({
    mode,
    categoryIds: categories.map(categoryId).sort((a, b) => Number(a) - Number(b)),
    ruleKey,
    ruleParams,
    fallbackWhenAbsent,
    providerKeys: [...providerKeys].sort(),
    providerConfigs: [...providerKeys].sort().reduce((result, key) => ({
      ...result,
      [key]: providerConfigs?.[key] || {},
    }), {}),
  });
}

function snapshotOf(settings) {
  return JSON.stringify({
    indexing: indexingSnapshotOf(settings),
    variantGroupingEnabled: settings.variantGroupingEnabled,
    variantPresentationEnabled: settings.variantPresentationEnabled,
  });
}

function providerSnapshotOf(providerKeys, providerConfigs) {
  return JSON.stringify([...providerKeys].sort().map(key => ({
    provider_key: key,
    config: providerConfigs?.[key] || {},
  })));
}

function catalogSnapshotOf(settings) {
  return indexingSnapshotOf({ ...settings, providerKeys: [], providerConfigs: {} });
}

function ensureSuccess(response, fallbackMessage) {
  if (response?.result === 'error') {
    throw new Error(response.error || fallbackMessage);
  }
  return response || {};
}

export default function ProductCatalogSettings({
  capabilities,
  catalogSelectorApi,
  payloadProviderApi,
  variantPresentationApi,
  categoryApi,
  onSaved,
  actionsContainer,
  actionsInHeader,
}) {
  const classes = useStyles();
  const localizeMessage = useLocalizedMessage();
  const [selectors, setSelectors] = useState([]);
  const [mode, setMode] = useState(MODE_ALL);
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState([]);
  const [ruleKey, setRuleKey] = useState('');
  const [ruleParams, setRuleParams] = useState({});
  const [fallbackWhenAbsent, setFallbackWhenAbsent] = useState(true);
  const [providers, setProviders] = useState([]);
  const [providerKeys, setProviderKeys] = useState([]);
  const [providerConfigs, setProviderConfigs] = useState({});
  const [variantGroupingEnabled, setVariantGroupingEnabled] = useState(true);
  const [savedVariantGroupingEnabled, setSavedVariantGroupingEnabled] = useState(true);
  const [variantPresentationEnabled, setVariantPresentationEnabled] = useState(true);
  const [savedVariantPresentationEnabled, setSavedVariantPresentationEnabled] = useState(true);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [savedCatalogSnapshot, setSavedCatalogSnapshot] = useState('');
  const [savedProviderSnapshot, setSavedProviderSnapshot] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [categoryResolutionWarning, setCategoryResolutionWarning] = useState('');
  const [savedMessage, setSavedMessage] = useState(false);

  const availabilityEnabled = Boolean(capabilities.availabilityRulesV1 && catalogSelectorApi);
  const providersEnabled = Boolean(
    PAYLOAD_PROVIDER_UI_ENABLED && capabilities.payloadProvidersV1 && payloadProviderApi,
  );
  const variantSettingsEnabled = Boolean(
    capabilities.variantPresentationSettingsV1 && variantPresentationApi,
  );
  const variantGroupingSettingsEnabled = Boolean(
    capabilities.variantGroupingV1 && variantSettingsEnabled,
  );

  const availableModes = useMemo(() => {
    const seen = new Set();
    return selectors.reduce((result, selector) => {
      const key = descriptorKey(selector, 'selector');
      const normalizedMode = selectorMode(key);
      if (!normalizedMode || seen.has(normalizedMode)) return result;
      seen.add(normalizedMode);
      result.push({ mode: normalizedMode, key });
      return result;
    }, []);
  }, [selectors]);

  const currentSnapshot = useMemo(() => snapshotOf({
    mode,
    categories,
    ruleKey,
    ruleParams,
    fallbackWhenAbsent,
    providerKeys,
    providerConfigs,
    variantGroupingEnabled,
    variantPresentationEnabled,
  }), [categories, fallbackWhenAbsent, mode, providerConfigs, providerKeys, ruleKey, ruleParams,
    variantGroupingEnabled, variantPresentationEnabled]);
  const currentCatalogSnapshot = useMemo(() => catalogSnapshotOf({
    mode,
    categories,
    ruleKey,
    ruleParams,
    fallbackWhenAbsent,
    providerKeys,
    providerConfigs,
  }), [categories, fallbackWhenAbsent, mode, providerConfigs, providerKeys, ruleKey, ruleParams]);
  const currentProviderSnapshot = useMemo(
    () => providerSnapshotOf(providerKeys, providerConfigs),
    [providerConfigs, providerKeys],
  );
  const isDirty = Boolean(savedSnapshot && currentSnapshot !== savedSnapshot);

  const loadSettings = useCallback(async () => {
    if (!capabilities.catalogSelectorsV1 || !catalogSelectorApi) return;
    setIsLoading(true);
    setLoadError('');
    setCategoryResolutionWarning('');

    try {
      const [selectorResult, activeResult] = await Promise.all([
        catalogSelectorApi.discoverSelectors(),
        catalogSelectorApi.getActive(),
      ]);
      const loadedSelectors = ensureSuccess(
        selectorResult,
        localizeMessage('search.page.dataSettings.error.loadModes'),
      ).selectors || [];
      const active = ensureSuccess(
        activeResult,
        localizeMessage('search.page.dataSettings.error.loadActiveMode'),
      );
      const activeMode = selectorMode(active.selector_key)
        || selectorMode(descriptorKey(loadedSelectors[0], 'selector'))
        || MODE_ALL;
      const excludedIds = Array.isArray(active.selector_config?.excluded_category_ids)
        ? active.selector_config.excluded_category_ids
        : [];

      let loadedCategories = [];
      if (excludedIds.length > 0) {
        if (categoryApi?.resolveCategories) {
          try {
            loadedCategories = await categoryApi.resolveCategories(excludedIds);
          } catch (resolveError) {
            loadedCategories = excludedIds.map((id) => ({
              id,
              name: localizeMessage('search.page.dataSettings.category.fallbackName', { id }),
            }));
            setCategoryResolutionWarning(
              localizeMessage('search.page.dataSettings.category.resolveWarning'),
            );
          }
        } else {
          loadedCategories = excludedIds.map((id) => ({
            id,
            name: localizeMessage('search.page.dataSettings.category.fallbackName', { id }),
          }));
        }
      }

      let loadedRules = [];
      let loadedRuleKey = '';
      let loadedRuleParams = {};
      let loadedFallback = true;
      if (availabilityEnabled) {
        const [rulesResult, activeRuleResult] = await Promise.all([
          catalogSelectorApi.discoverAvailabilityRules(),
          catalogSelectorApi.getActiveAvailabilityCriteria(),
        ]);
        loadedRules = ensureSuccess(
          rulesResult,
          localizeMessage('search.page.dataSettings.error.loadRules'),
        ).rules || [];
        const activeCriteria = ensureSuccess(
          activeRuleResult,
          localizeMessage('search.page.dataSettings.error.loadActiveRule'),
        ).criteria || {};
        loadedRuleKey = activeCriteria.available_rule_key
          || descriptorKey(loadedRules[0], 'rule')
          || '';
        loadedRuleParams = activeCriteria.rule_params || {};
        loadedFallback = activeCriteria.fallback_when_absent !== false;
      }

      let loadedProviders = [];
      let loadedProviderKeys = [];
      let loadedProviderConfigs = {};
      if (providersEnabled) {
        const [providerResult, activeProviderResult] = await Promise.all([
          payloadProviderApi.discoverProviders(),
          payloadProviderApi.getActiveProviderConfigs(),
        ]);
        loadedProviders = ensureSuccess(
          providerResult,
          localizeMessage('search.page.dataSettings.error.loadProviders'),
        ).providers || [];
        const activeProviders = ensureSuccess(
          activeProviderResult,
          localizeMessage('search.page.dataSettings.error.loadActiveProviders'),
        );
        const activeProviderConfigs = activeProviders.providers || activeProviders.configs || [];
        loadedProviderKeys = activeProviderConfigs
          .map((provider) => descriptorKey(provider, 'provider'))
          .filter(Boolean);
        loadedProviderConfigs = activeProviderConfigs.reduce((configs, provider) => {
          const key = descriptorKey(provider, 'provider');
          if (key) configs[key] = provider.config || provider.params || {};
          return configs;
        }, {});
      }

      let loadedVariantPresentationEnabled = true;
      let loadedVariantGroupingEnabled = true;
      if (variantSettingsEnabled) {
        const variantSettings = ensureSuccess(
          await variantPresentationApi.getSettings(),
          localizeMessage('search.page.dataSettings.error.loadVariantSettings'),
        );
        loadedVariantGroupingEnabled = variantSettings.grouped !== false;
        loadedVariantPresentationEnabled = variantSettings.enabled !== false;
      }

      setSelectors(loadedSelectors);
      setMode(activeMode);
      setCategories(loadedCategories);
      setRules(loadedRules);
      setRuleKey(loadedRuleKey);
      setRuleParams(loadedRuleParams);
      setFallbackWhenAbsent(loadedFallback);
      setProviders(loadedProviders);
      setProviderKeys(loadedProviderKeys);
      setProviderConfigs(loadedProviderConfigs);
      setVariantGroupingEnabled(loadedVariantGroupingEnabled);
      setSavedVariantGroupingEnabled(loadedVariantGroupingEnabled);
      setVariantPresentationEnabled(loadedVariantPresentationEnabled);
      setSavedVariantPresentationEnabled(loadedVariantPresentationEnabled);
      setSavedSnapshot(snapshotOf({
        mode: activeMode,
        categories: loadedCategories,
        ruleKey: loadedRuleKey,
        ruleParams: loadedRuleParams,
        fallbackWhenAbsent: loadedFallback,
        providerKeys: loadedProviderKeys,
        providerConfigs: loadedProviderConfigs,
        variantGroupingEnabled: loadedVariantGroupingEnabled,
        variantPresentationEnabled: loadedVariantPresentationEnabled,
      }));
      setSavedCatalogSnapshot(catalogSnapshotOf({
        mode: activeMode,
        categories: loadedCategories,
        ruleKey: loadedRuleKey,
        ruleParams: loadedRuleParams,
        fallbackWhenAbsent: loadedFallback,
        providerKeys: loadedProviderKeys,
        providerConfigs: loadedProviderConfigs,
      }));
      setSavedProviderSnapshot(providerSnapshotOf(loadedProviderKeys, loadedProviderConfigs));
    } catch (error) {
      setLoadError(
        error?.message || localizeMessage('search.page.dataSettings.error.loadSettings'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [availabilityEnabled, capabilities.catalogSelectorsV1, catalogSelectorApi, categoryApi,
    payloadProviderApi, providersEnabled, variantPresentationApi, variantSettingsEnabled]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setValidationError('');
    setSavedMessage(false);
  };

  const handleProviderToggle = (key) => {
    setProviderKeys((current) => (
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    ));
    setSavedMessage(false);
  };

  const handleDiscard = () => {
    loadSettings();
    setSaveError('');
    setValidationError('');
    setSavedMessage(false);
  };

  const handleSave = async () => {
    if (mode === MODE_EXCLUDED && categories.length === 0) {
      setValidationError(localizeMessage('search.page.dataSettings.error.categoryRequired'));
      return;
    }
    if (mode === MODE_AVAILABLE && availabilityEnabled && !ruleKey) {
      setValidationError(localizeMessage('search.page.dataSettings.error.ruleRequired'));
      return;
    }

    const selectedMode = availableModes.find((item) => item.mode === mode);
    if (!selectedMode) {
      setSaveError(localizeMessage('search.page.dataSettings.error.unsupportedMode'));
      return;
    }

    setIsSaving(true);
    setSaveError('');
    setValidationError('');
    setSavedMessage(false);
    let persisted = false;
    const catalogRequiresReindex = currentCatalogSnapshot !== savedCatalogSnapshot;
    const providersChanged = providersEnabled
      && currentProviderSnapshot !== savedProviderSnapshot;
    const variantPayloadChanged = variantPresentationEnabled
      !== savedVariantPresentationEnabled;
    const requiresReindex = catalogRequiresReindex || providersChanged || variantPayloadChanged;
    const variantGroupingChanged = variantGroupingSettingsEnabled
      && variantGroupingEnabled !== savedVariantGroupingEnabled;
    const variantPresentationChanged = variantPresentationEnabled
      !== savedVariantPresentationEnabled;

    try {
      if (catalogRequiresReindex && mode === MODE_AVAILABLE && availabilityEnabled) {
        ensureSuccess(
          await catalogSelectorApi.setAvailabilityCriteria(ruleKey, ruleParams, fallbackWhenAbsent),
          localizeMessage('search.page.dataSettings.error.saveRule'),
        );
        persisted = true;
      }

      if (catalogRequiresReindex) {
        const selectorConfig = mode === MODE_EXCLUDED
          ? { excluded_category_ids: categories.map(categoryId) }
          : {};
        ensureSuccess(
          await catalogSelectorApi.setSelector(selectedMode.key, selectorConfig),
          localizeMessage('search.page.dataSettings.error.saveMode'),
        );
        persisted = true;
      }

      if (providersChanged) {
        ensureSuccess(
          await payloadProviderApi.saveActiveProviderConfigs(
            providerKeys.map((providerKey) => ({
              provider_key: providerKey,
              config: providerConfigs[providerKey] || {},
            })),
          ),
          localizeMessage('search.page.dataSettings.error.saveProviders'),
        );
        persisted = true;
      }

      if (variantSettingsEnabled && (variantGroupingChanged || variantPresentationChanged)) {
        ensureSuccess(
          await variantPresentationApi.saveSettings(variantGroupingSettingsEnabled
            ? { grouped: variantGroupingEnabled, enabled: variantPresentationEnabled }
            : { enabled: variantPresentationEnabled }),
          localizeMessage('search.page.dataSettings.error.saveVariantSettings'),
        );
        persisted = true;
      }

      setSavedSnapshot(currentSnapshot);
      setSavedCatalogSnapshot(currentCatalogSnapshot);
      setSavedProviderSnapshot(currentProviderSnapshot);
      setSavedVariantGroupingEnabled(variantGroupingEnabled);
      setSavedVariantPresentationEnabled(variantPresentationEnabled);
      setSavedMessage(true);
      if (onSaved) onSaved({ requiresReindex });
    } catch (error) {
      setSaveError(
        persisted
          ? localizeMessage('search.page.dataSettings.error.partialSave', {
            error: error?.message || localizeMessage('search.page.dataSettings.error.saveAll'),
          })
          : error?.message || localizeMessage('search.page.dataSettings.error.saveSettings'),
      );
      if (persisted && onSaved) onSaved({ requiresReindex });
    } finally {
      setIsSaving(false);
    }
  };

  if (!capabilities.catalogSelectorsV1) return null;

  if (isLoading) {
    return (
      <SettingsCard className={classes.section} data-testid="product-catalog-settings-loading">
        <Box display="flex" alignItems="center">
          <CircularProgress size={20} />
          <Box ml={1.5}>
            <Typography variant="body2">
              {localizeMessage('search.page.dataSettings.loading')}
            </Typography>
          </Box>
        </Box>
      </SettingsCard>
    );
  }

  if (loadError) {
    return (
      <Box className={classes.section} data-testid="product-catalog-settings-error">
        <Alert
          severity="error"
          action={(
            <Button color="inherit" size="small" onClick={loadSettings}>
              {localizeMessage('search.page.dataSettings.retry')}
            </Button>
          )}
        >
          {loadError}
        </Alert>
      </Box>
    );
  }

  const selectedRule = rules.find((rule) => descriptorKey(rule, 'rule') === ruleKey);
  const selectedRuleKeys = RULE_MESSAGE_KEYS[ruleKey];
  const selectedRuleCopy = selectedRuleKeys ? {
    title: localizeMessage(selectedRuleKeys.title),
    description: localizeMessage(selectedRuleKeys.description),
  } : {
    title: selectedRule?.label || ruleKey,
    description: selectedRule?.description || '',
  };
  const showFallback = mode === MODE_AVAILABLE && availabilityEnabled
    && ruleKey !== 'ps_available_for_order' && ruleKey !== 'available_for_order';
  const showStockPolicyParam = ruleKey === 'ps_stock_flag' || ruleKey === 'stock_flag';
  const showCombinationRule = ruleKey === 'ps_combination_in_stock' || ruleKey === 'combination_in_stock';

  const actions = (
    <Box
      className={`${classes.actionRow} ${actionsContainer ? classes.headerActionRow : ''}`}
      data-testid="product-settings-actions"
    >
      <Button
        variant="outlined"
        color="secondary"
        onClick={handleDiscard}
        disabled={!isDirty || isSaving}
        data-testid="discard-product-settings"
      >
        {localizeMessage('common.discard')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        disabled={!isDirty || isSaving}
        startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        data-testid="save-product-settings"
      >
        {isSaving
          ? localizeMessage('common.saving')
          : localizeMessage('common.save')}
      </Button>
    </Box>
  );

  return (
    <Box className={classes.root} data-testid="product-catalog-settings">
      <Box className={classes.section}>
        <SettingsCard>
          <Typography variant="h6" gutterBottom>
            {localizeMessage('search.page.dataSettings.catalog.title')}
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.intro}>
            {localizeMessage('search.page.dataSettings.catalog.description')}
          </Typography>

          {availableModes.length === 0 && (
            <Alert severity="warning">
              {localizeMessage('search.page.dataSettings.catalog.noModes')}
            </Alert>
          )}

          <div
            className={classes.options}
            role="radiogroup"
            aria-label={localizeMessage('search.page.dataSettings.catalog.radioLabel')}
          >
            {availableModes.map((item) => {
              const messageKeys = MODE_MESSAGE_KEYS[item.mode];
              const copy = {
                title: localizeMessage(messageKeys.title),
                description: localizeMessage(messageKeys.description),
              };
              const selected = mode === item.mode;
              return (
                <div
                  key={item.mode}
                  className={classes.optionGroup}
                  data-testid={`product-mode-group-${item.mode}`}
                >
                  <label
                    className={`${classes.option} ${selected ? classes.selectedOption : ''}`}
                    data-testid={`product-mode-${item.mode}`}
                  >
                    <Radio
                      className={classes.optionRadio}
                      color="primary"
                      checked={selected}
                      onChange={() => handleModeChange(item.mode)}
                      value={item.mode}
                      inputProps={{ 'aria-label': copy.title }}
                    />
                    <span className={classes.optionBody}>
                      <span className={classes.optionTitle}>
                        <Typography component="span" variant="subtitle2">{copy.title}</Typography>
                        {item.mode === MODE_ALL && (
                          <Chip
                            label={localizeMessage('search.page.dataSettings.recommended')}
                            size="small"
                            className={classes.recommended}
                          />
                        )}
                      </span>
                      <Typography component="span" variant="body2" color="textSecondary">
                        {copy.description}
                      </Typography>
                    </span>
                  </label>

                  {item.mode === MODE_AVAILABLE && selected && availabilityEnabled && (
                    <Box className={classes.nested} data-testid="availability-settings">
                      <FormControl variant="outlined" size="small" className={classes.ruleSelect}>
                        <FormLabel component="legend">
                          {localizeMessage('search.page.dataSettings.availability.title')}
                        </FormLabel>
                        <Box mt={1}>
                          <Select
                            fullWidth
                            value={ruleKey}
                            onChange={(event) => {
                              setRuleKey(event.target.value);
                              setRuleParams({});
                              setSavedMessage(false);
                            }}
                            inputProps={{
                              'aria-label': localizeMessage('search.page.dataSettings.availability.ruleLabel'),
                            }}
                            data-testid="availability-rule-select"
                          >
                            {rules.map((rule) => {
                              const key = descriptorKey(rule, 'rule');
                              const ruleMessageKeys = RULE_MESSAGE_KEYS[key];
                              return (
                                <MenuItem key={key} value={key}>
                                  {ruleMessageKeys
                                    ? localizeMessage(ruleMessageKeys.title)
                                    : rule.label || key}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </Box>
                      </FormControl>
                      {selectedRuleCopy.description && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          className={classes.ruleDescription}
                        >
                          {selectedRuleCopy.description}
                        </Typography>
                      )}
                      {showStockPolicyParam && (
                        <FormControlLabel
                          control={(
                            <Checkbox
                              color="primary"
                              checked={ruleParams.allow_out_of_stock_orders === true}
                              onChange={(event) => {
                                setRuleParams({
                                  ...ruleParams,
                                  allow_out_of_stock_orders: event.target.checked,
                                });
                                setSavedMessage(false);
                              }}
                              data-testid="allow-out-of-stock-orders"
                            />
                          )}
                          label={localizeMessage('search.page.dataSettings.availability.allowOutOfStock')}
                        />
                      )}
                      {showFallback && (
                        <FormControlLabel
                          control={(
                            <Checkbox
                              color="primary"
                              checked={fallbackWhenAbsent}
                              onChange={(event) => {
                                setFallbackWhenAbsent(event.target.checked);
                                setSavedMessage(false);
                              }}
                              data-testid="availability-fallback"
                            />
                          )}
                          label={showCombinationRule
                            ? localizeMessage('search.page.dataSettings.availability.fallbackCombination')
                            : localizeMessage('search.page.dataSettings.availability.fallbackStockRow')}
                        />
                      )}
                    </Box>
                  )}

                  {item.mode === MODE_EXCLUDED && selected && (
                    <Box className={classes.nested}>
                      {!categoryApi?.searchCategories && (
                        <Box mb={1.5}>
                          <Alert severity="error">
                            {localizeMessage('search.page.dataSettings.category.unavailable')}
                          </Alert>
                        </Box>
                      )}
                      <CategoryExclusionPicker
                        value={categories}
                        onChange={(nextCategories) => {
                          setCategories(nextCategories);
                          setValidationError('');
                          setSavedMessage(false);
                        }}
                        onSearch={(query) => categoryApi.searchCategories(query, 12)}
                        disabled={!categoryApi?.searchCategories}
                        error={validationError}
                      />
                      {categoryResolutionWarning && (
                        <Box mt={1}>
                          <Alert severity="warning">{categoryResolutionWarning}</Alert>
                        </Box>
                      )}
                    </Box>
                  )}
                </div>
              );
            })}
          </div>
        </SettingsCard>
      </Box>

      <Box className={classes.section} data-testid="product-variants-status">
        <SettingsCard>
          <Box className={classes.variantHeader}>
            <Box>
              <Typography variant="h6" gutterBottom>
                {localizeMessage('search.page.dataSettings.variants.title')}
              </Typography>
              <Typography variant="body2" color="textSecondary" className={classes.intro}>
                {localizeMessage('search.page.dataSettings.variants.description')}
              </Typography>
            </Box>
            {!variantSettingsEnabled && (
              <Chip
                size="small"
                className={classes.automatic}
                label={localizeMessage('search.page.dataSettings.variants.automatic')}
              />
            )}
          </Box>

          {variantGroupingSettingsEnabled && (
            <>
              <div
                className={classes.options}
                role="radiogroup"
                aria-label={localizeMessage('search.page.dataSettings.variants.modeLabel')}
                data-testid="variant-grouping-modes"
              >
                {[true, false].map((grouped) => {
                  const key = grouped ? 'group' : 'individual';
                  const selected = variantGroupingEnabled === grouped;
                  return (
                    <label
                      key={key}
                      className={`${classes.option} ${selected ? classes.selectedOption : ''}`}
                      data-testid={`variant-grouping-${key}`}
                    >
                      <Radio
                        className={classes.optionRadio}
                        color="primary"
                        checked={selected}
                        onChange={() => {
                          setVariantGroupingEnabled(grouped);
                          setSavedMessage(false);
                        }}
                        value={key}
                        inputProps={{
                          'aria-label': localizeMessage(`search.page.dataSettings.variants.${key}.title`),
                        }}
                      />
                      <span className={classes.optionBody}>
                        <span className={classes.optionTitle}>
                          <Typography component="span" variant="subtitle2">
                            {localizeMessage(`search.page.dataSettings.variants.${key}.title`)}
                          </Typography>
                          {grouped && (
                            <Chip
                              label={localizeMessage('search.page.dataSettings.recommended')}
                              size="small"
                              className={classes.recommended}
                            />
                          )}
                        </span>
                        <Typography component="span" variant="body2" color="textSecondary">
                          {localizeMessage(`search.page.dataSettings.variants.${key}.description`)}
                        </Typography>
                      </span>
                    </label>
                  );
                })}
              </div>
              {variantGroupingEnabled && (
                <Box className={classes.nested} mt={1.5} data-testid="variant-payload-settings">
                  <FormControlLabel
                    control={(
                      <Checkbox
                        color="primary"
                        checked={variantPresentationEnabled}
                        onChange={(event) => {
                          setVariantPresentationEnabled(event.target.checked);
                          setSavedMessage(false);
                        }}
                        inputProps={{
                          'aria-label': localizeMessage(
                            'search.page.dataSettings.variants.payload.include',
                          ),
                        }}
                      />
                    )}
                    label={localizeMessage('search.page.dataSettings.variants.payload.include')}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {localizeMessage('search.page.dataSettings.variants.payload.description')}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {variantSettingsEnabled && !variantGroupingSettingsEnabled && (
            <Box className={classes.nested} mt={1.5} data-testid="variant-payload-settings">
              <FormControlLabel
                control={(
                  <Checkbox
                    color="primary"
                    checked={variantPresentationEnabled}
                    onChange={(event) => {
                      setVariantPresentationEnabled(event.target.checked);
                      setSavedMessage(false);
                    }}
                    inputProps={{
                      'aria-label': localizeMessage(
                        'search.page.dataSettings.variants.include.title',
                      ),
                    }}
                  />
                )}
                label={localizeMessage('search.page.dataSettings.variants.include.title')}
              />
              <Typography variant="body2" color="textSecondary">
                {localizeMessage('search.page.dataSettings.variants.include.description')}
              </Typography>
            </Box>
          )}

        </SettingsCard>
      </Box>

      {providersEnabled && (
        <Box className={classes.section} data-testid="additional-product-data">
          <SettingsCard>
            <Box className={classes.providerIdentity}>
              <Box className={classes.providerIcon} aria-hidden="true">
                <ListAltOutlinedIcon />
              </Box>
              <Box className={classes.providerCopy}>
                <Typography variant="h6">
                  {localizeMessage('search.page.dataSettings.provider.title')}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.providerDescription}
                >
                  {localizeMessage('search.page.dataSettings.provider.description')}
                </Typography>
              </Box>
            </Box>
            <Box className={classes.providerOptions}>
              {providers.map((provider) => {
                const key = descriptorKey(provider, 'provider');
                const descriptionId = `product-provider-${encodeURIComponent(key)}-description`;
                const messageKeys = PROVIDER_MESSAGE_KEYS[key];
                const copy = messageKeys ? {
                  title: localizeMessage(messageKeys.title),
                  description: localizeMessage(messageKeys.description),
                } : {
                  title: provider.label || key,
                  description: provider.description || '',
                };
                return (
                  <FormControlLabel
                    className={classes.providerRow}
                    classes={{ label: classes.providerLabel }}
                    key={key}
                    control={(
                      <Checkbox
                        color="primary"
                        checked={providerKeys.includes(key)}
                        onChange={() => handleProviderToggle(key)}
                        inputProps={{
                          'aria-label': copy.title,
                          'aria-describedby': copy.description ? descriptionId : undefined,
                        }}
                        data-testid={`product-provider-${key}`}
                      />
                    )}
                    label={(
                      <>
                        <Typography component="span" variant="body1" display="block">
                          {copy.title}
                        </Typography>
                        {copy.description && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="textSecondary"
                            display="block"
                            id={descriptionId}
                          >
                            {copy.description}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                );
              })}
            </Box>
          </SettingsCard>
        </Box>
      )}

      {saveError && <Box mb={2}><Alert severity="error" data-testid="settings-save-error">{saveError}</Alert></Box>}
      {savedMessage && (
        <Box mb={2}>
          <Alert severity="success" data-testid="settings-save-success">
            {localizeMessage('search.page.dataSettings.saved')}
          </Alert>
        </Box>
      )}

      {actionsContainer
        ? createPortal(actions, actionsContainer)
        : !actionsInHeader && actions}
    </Box>
  );
}

ProductCatalogSettings.propTypes = {
  capabilities: PropTypes.shape({
    catalogSelectorsV1: PropTypes.bool,
    availabilityRulesV1: PropTypes.bool,
    payloadProvidersV1: PropTypes.bool,
    variantPresentationSettingsV1: PropTypes.bool,
    variantGroupingV1: PropTypes.bool,
  }),
  catalogSelectorApi: PropTypes.shape({
    discoverSelectors: PropTypes.func.isRequired,
    getActive: PropTypes.func.isRequired,
    setSelector: PropTypes.func.isRequired,
    discoverAvailabilityRules: PropTypes.func,
    getActiveAvailabilityCriteria: PropTypes.func,
    setAvailabilityCriteria: PropTypes.func,
  }),
  payloadProviderApi: PropTypes.shape({
    discoverProviders: PropTypes.func.isRequired,
    getActiveProviderConfigs: PropTypes.func.isRequired,
    saveActiveProviderConfigs: PropTypes.func.isRequired,
  }),
  variantPresentationApi: PropTypes.shape({
    getSettings: PropTypes.func.isRequired,
    saveSettings: PropTypes.func.isRequired,
  }),
  categoryApi: PropTypes.shape({
    searchCategories: PropTypes.func.isRequired,
    resolveCategories: PropTypes.func.isRequired,
  }),
  onSaved: PropTypes.func,
  actionsContainer: PropTypes.object,
  actionsInHeader: PropTypes.bool,
};

ProductCatalogSettings.defaultProps = {
  capabilities: {},
  catalogSelectorApi: serviceRegistry.catalogSelectorApi,
  payloadProviderApi: serviceRegistry.payloadProviderApi,
  variantPresentationApi: serviceRegistry.variantPresentationApi,
  categoryApi: serviceRegistry.emptyQueryApi,
  onSaved: null,
  actionsContainer: null,
  actionsInHeader: false,
};
